import type { MePlan, User } from './api'
import { currentLang, t, type Key } from './i18n'

/*
 * The user's plan in words (BILL-B5a; docs/billing/design.md §6, §10.1): what GET /api/me says
 * about the plan, turned into the label of the Studio bar and the lines of Account → Plan & usage.
 * No payments yet (owner decision Q10): "Choose a plan" opens the site's pricing page.
 * plan: null (the box, BILLING_MODE=off) = no plan anywhere in the cabinet.
 */

/** The pricing page configured for this build. */
export function pricingUrl(): string {
  const url = currentLang() === 'es' ? import.meta.env.VITE_PRICING_URL_ES : import.meta.env.VITE_PRICING_URL
  if (!url) throw new Error('Pricing URL is not configured')
  return url
}

export const BILLING_PATH = '/account/billing'

/** The cabinet shows a plan only when the center bills (not the box). */
export function hasPlan(u: User | null | undefined): u is User & { plan: MePlan } {
  return !!u?.plan
}

const DAY = 86_400_000

/** Whole days until `iso`, rounded up (a trial ending in 3 h has 1 day left); 0 when past. */
export function daysLeft(iso: string | undefined, now: number): number {
  if (!iso) return 0
  return Math.max(0, Math.ceil((Date.parse(iso) - now) / DAY))
}

/** Dates are day first in every language, EN too: "30 Nov" (owner, 24.10, BILL-B5a Q4); en-GB gives that order. */
const dateLocale = () => (currentLang() === 'en' ? 'en-GB' : currentLang())

/** "30 Nov" in the cabinet's language (UTC: plan dates are the center's). */
export function shortDate(iso: string): string {
  return new Intl.DateTimeFormat(dateLocale(), { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(iso))
}

/** "30 Nov 2026". */
export function longDate(iso: string): string {
  return new Intl.DateTimeFormat(dateLocale(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(iso))
}

export function num(n: number): string {
  return new Intl.NumberFormat(currentLang()).format(n)
}

/** The plan badge's weight (PlansAndLimits): monochrome, rises with the tier; a plan is never a state. DS: PL-02 (service plans mapped onto the four tiers). */
export function badgeClass(p: MePlan): 'free' | 'creator' | 'pro' | 'business' {
  switch (p.id) {
    case 'creator':
    case 'legacy':
      return 'creator'
    case 'pro':
      return 'pro'
    case 'business':
    case 'ambassador':
      return 'business'
    default:
      return 'free'
  }
}

/** Why new broadcasts don't start (plan.access no_new_streams), in words; undefined = they do. */
export function accessText(p: MePlan): string | undefined {
  if (p.access === 'full') return undefined
  switch (p.access_reason) {
    case 'account_frozen':
      return t('plan.access.account_frozen')
    case 'account_streams_blocked':
      return t('plan.access.account_streams_blocked')
    case 'plan_payment_disputed':
      return t('plan.access.plan_payment_disputed')
    case 'plan_paused':
      return t('plan.access.plan_paused')
    default:
      return t('plan.access.other')
  }
}

/** The free floor after a trial, a cancellation or an unpaid plan: "Plan ended — Free". */
export function planEnded(p: MePlan): boolean {
  return p.id === 'free' && (p.status === 'trial_expired' || p.status === 'canceled' || p.status === 'past_due')
}

/**
 * The plan and its status in one line (the page's heading line):
 * "Trial · 9 days left", "Free", "Business — gift until 30 Nov", "Ambassador · VIP", "Legacy until 21 Jan".
 */
export function statusLine(u: User & { plan: MePlan }, now: number): string {
  const p = u.plan
  if (p.status === 'trialing' && p.id === 'trial') {
    const d = daysLeft(p.trial_ends_at, now)
    return d > 0 ? t('plan.trialLeft', { days: daysText(d) }) : t('plan.trialEnds')
  }
  if (p.id === 'ambassador') return u.badges?.includes('vip') ? t('plan.ambassadorVip', { name: p.name }) : p.name
  if (p.id === 'legacy') return p.until ? t('plan.legacyUntil', { date: longDate(p.until) }) : p.name
  if (p.source === 'grant') return p.until ? t('plan.giftUntil', { name: p.name, date: shortDate(p.until) }) : t('plan.gift', { name: p.name })
  if (planEnded(p)) return t('plan.endedFree', { name: p.name })
  if (p.status === 'past_due') return t('plan.paymentFailed', { name: p.name })
  if (p.status === 'canceled' && p.period_end) return t('plan.canceledUntil', { name: p.name, date: shortDate(p.period_end) })
  return p.name
}

/** "1 day", "9 days", "2 дня", "5 дней": the plural form of the cabinet's language. */
export function daysText(n: number): string {
  const f = new Intl.PluralRules(currentLang()).select(n)
  const key: Key = f === 'one' ? 'plan.days.one' : f === 'few' ? 'plan.days.few' : f === 'many' ? 'plan.days.many' : 'plan.days.other'
  return t(key, { n: num(n) })
}

export type BarLabel = { text: string; tone: 'idle' | 'warn' | 'fail' }

/**
 * The Studio bar's plan label (design §10.1): only what the streamer should notice —
 * the trial's days, a plan that ended, a failed payment, new broadcasts blocked. A paid plan in
 * good standing shows nothing there (it is on Account → Plan & usage).
 */
export function barLabel(u: User | null | undefined, now: number): BarLabel | null {
  if (!hasPlan(u)) return null
  const p = u.plan
  if (p.access !== 'full') {
    return { text: p.access_reason === 'account_frozen' ? t('plan.bar.frozen') : p.access_reason === 'plan_paused' ? t('plan.bar.paused') : t('plan.bar.blocked'), tone: 'fail' }
  }
  if (p.status === 'trialing' && p.id === 'trial') {
    const d = daysLeft(p.trial_ends_at, now)
    return { text: d > 0 ? t('plan.bar.trial', { days: daysText(d) }) : t('plan.bar.trialEnds'), tone: d <= 3 ? 'warn' : 'idle' }
  }
  if (planEnded(p)) return { text: t('plan.bar.ended'), tone: 'warn' }
  if (p.status === 'past_due') return { text: t('plan.bar.paymentFailed'), tone: 'warn' }
  return null
}

/* ── limits ──────────────────────────────────────────────────────────── */

export type LimitValue = number | string | null | undefined

/** The limits of the page, in the order of the pricing table (pricing.json limit_keys). */
export const LIMIT_ROWS: { key: string; label: Key; unit: 'count' | 'hours' | 'seconds' | 'preset' }[] = [
  { key: 'destinations', label: 'plan.limit.destinations', unit: 'count' },
  { key: 'concurrent_streams', label: 'plan.limit.concurrent_streams', unit: 'count' },
  { key: 'stream_hours_month', label: 'plan.limit.stream_hours_month', unit: 'hours' },
  { key: 'ingest_max', label: 'plan.limit.ingest_max', unit: 'preset' },
  { key: 'source_grace_max_s', label: 'plan.limit.source_grace_max_s', unit: 'seconds' },
  { key: 'captions_hours_month', label: 'plan.limit.captions_hours_month', unit: 'hours' },
  { key: 'transcode_outputs', label: 'plan.limit.transcode_outputs', unit: 'count' },
  { key: 'transcode_hours_month', label: 'plan.limit.transcode_hours_month', unit: 'hours' },
  { key: 'translation_lang_hours_month', label: 'plan.limit.translation_lang_hours_month', unit: 'hours' },
]

/** "2 min", "45 s", "10 min" — the system's time words (UsageMeters «Words»). */
export function secondsText(s: number): string {
  if (s < 60) return t('plan.unit.s', { n: num(s) })
  const m = Math.floor(s / 60)
  const r = s % 60
  return r ? t('plan.unit.minS', { m: num(m), s: num(r) }) : t('plan.unit.min', { n: num(m) })
}

/** A limit's value in words: "No limit", "20 h a month", "2 min", "1080p60". A missing key: undefined. */
export function limitText(unit: (typeof LIMIT_ROWS)[number]['unit'], v: LimitValue): string | undefined {
  if (v === undefined) return undefined
  if (v === null) return t('plan.noLimit')
  if (typeof v === 'string') return v
  switch (unit) {
    case 'hours':
      return v === 0 ? t('plan.notIncluded') : t('plan.unit.hMonth', { n: num(v) })
    case 'seconds':
      return secondsText(v)
    default:
      return v === 0 ? t('plan.notIncluded') : num(v)
  }
}

/** The features the page lists as included / not on this plan (design §3.4, pricing.json features). */
export const FEATURE_ROWS: { id: string; label: Key; derived?: (l: Record<string, LimitValue>) => boolean; soon?: (l: Record<string, LimitValue>) => boolean }[] = [
  { id: 'chat', label: 'plan.feature.chat' },
  { id: 'captions', label: 'plan.feature.captions' },
  // Coming soon in the catalog (pricing.json): the center leaves it out of features, the plan has its hours
  { id: 'caption_translation', label: 'plan.feature.caption_translation', soon: (l) => typeof l.translation_lang_hours_month === 'number' && l.translation_lang_hours_month > 0 },
  // transcoding is a limit, not a feature flag: on with transcoded outputs on the plan
  { id: 'transcode', label: 'plan.feature.transcode', derived: (l) => typeof l.transcode_outputs !== 'number' || l.transcode_outputs > 0 },
  { id: 'vertical_9_16', label: 'plan.feature.vertical_9_16' },
  { id: 'srt_ingest', label: 'plan.feature.srt_ingest' },
  { id: 'api_tokens', label: 'plan.feature.api_tokens' },
  { id: 'no_watermark', label: 'plan.feature.no_watermark' },
]

export function hasFeature(p: MePlan, limits: Record<string, LimitValue>, row: (typeof FEATURE_ROWS)[number]): boolean {
  return row.derived ? row.derived(limits) : p.features.includes(row.id)
}

/** "Up to 2 min on Creator" for a source grace field; undefined without a plan or a ceiling. */
export function graceCeiling(u: User | null | undefined): { s: number; plan: string } | undefined {
  if (!hasPlan(u)) return undefined
  const v = u.limits?.source_grace_max_s
  return typeof v === 'number' ? { s: v, plan: u.plan.name } : undefined
}

/* ── refusals ─────────────────────────────────────────────────────────── */

/**
 * Plan and account refusal codes (design §4.3; Hub.SetRefusal → input state_reason, and the
 * `code` of a 409/403/422 answer): the text for the streamer. Unknown codes: undefined.
 */
export function refusalText(code: string | undefined, u: User | null | undefined, data?: unknown): string | undefined {
  // a 409/422 answer carries the limit it decided on (planDenied: limit, used, plan_id)
  const lim = (data as { limit?: unknown } | undefined)?.limit
  const l: Record<string, LimitValue> = { ...(u?.limits ?? {}) }
  if (typeof lim === 'number' && code === 'plan_limit_destinations') l.destinations = lim
  if (typeof lim === 'number' && code === 'plan_limit_source_grace') l.source_grace_max_s = lim
  const plan = u?.plan?.name ?? t('plan.yourPlan')
  switch (code) {
    case 'plan_limit_concurrent': {
      const n = typeof l.concurrent_streams === 'number' ? l.concurrent_streams : 1
      return t(n === 1 ? 'plan.refuse.concurrent1' : 'plan.refuse.concurrentN', { n, plan })
    }
    case 'plan_limit_stream_hours':
      return t('plan.refuse.streamHours', { plan })
    case 'plan_limit_destinations':
      return t('plan.refuse.destinations', { n: typeof l.destinations === 'number' ? l.destinations : '', plan })
    case 'plan_limit_source_grace':
      return t('plan.refuse.sourceGrace', { max: typeof l.source_grace_max_s === 'number' ? secondsText(l.source_grace_max_s) : '', plan })
    case 'plan_no_captions':
      return t('plan.refuse.noCaptions', { plan })
    case 'plan_limit_captions_hours':
      return t('plan.refuse.captionsHours', { plan })
    case 'plan_captions_overrun':
      return t('plan.refuse.captionsOverrun', { plan })
    case 'plan_no_vertical':
      return t('plan.refuse.noVertical', { plan })
    case 'plan_paused':
    case 'plan_payment_disputed':
    case 'account_frozen':
    case 'account_streams_blocked':
      return t(`plan.access.${code}`)
  }
  return undefined
}

/** Input refusals the Studio explains with a link to the plan page. */
export const PLAN_REFUSALS = ['plan_limit_concurrent', 'plan_limit_stream_hours', 'plan_paused', 'plan_payment_disputed', 'account_frozen', 'account_streams_blocked', 'plan_no_vertical'] as const
